import React, { useState, useEffect } from 'react';
import './Blogs.css';
import PopupCard from '../components/PopupCard';

// load all images from src/assets so we can resolve image paths stored in the DB
const assetImages = require.context('../assets', false, /\.(png|jpe?g|svg)$/);

const getApiBase = () => {
    const raw = (process.env.REACT_APP_API_URL || '').trim();
    if (!raw) return '';

    // In build-time env from local dev, ignore localhost API when running in production host.
    if (typeof window !== 'undefined') {
        const host = window.location.hostname;
        if (!/^localhost$|^127\.0\.0\.1$/.test(host) && raw.includes('localhost')) {
            return '';
        }
    }

    return raw.replace(/\/$/, '');
};

function resolveImage(url) {
    if (!url) return null;

    let normalized = url;

    // Normalize relative storage path to absolute path in app root
    if (!/^https?:\/\//i.test(normalized)) {
        if (normalized.startsWith('uploads/')) {
            const apiBase = getApiBase();
            if (apiBase) {
                normalized = `${apiBase}/${normalized}`;
            } else {
                normalized = `/${normalized}`;
            }
        }

        if (normalized.startsWith('/')) {
            // keep as absolute path for local/public access
        } else if (!/^https?:\/\//i.test(normalized)) {
            // unknown relative path, leave as-is
            return normalized;
        }
    }

    if (typeof window !== 'undefined' && !normalized.startsWith('http')) {
        if (normalized.startsWith('/')) {
            normalized = `${window.location.origin}${normalized}`;
        }

        // If app is hosted in subfolder (e.g., /hart-elegance), map absolute path to subfolder location.
        const pathParts = window.location.pathname.split('/').filter(Boolean);
        const appBase = pathParts.length > 0 ? `/${pathParts[0]}` : '';
        if (appBase && normalized.startsWith(`${window.location.origin}/`) && !normalized.startsWith(`${window.location.origin}${appBase}/`)) {
            normalized = `${window.location.origin}${appBase}${normalized.slice(window.location.origin.length)}`;
        }
    }

    return normalized;
}

function Blogs() {
    const [blogs, setBlogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedBlog, setSelectedBlog] = useState(null);

    const [newTitle, setNewTitle] = useState('');
    const [newContent, setNewContent] = useState('');
    const [newAuthor, setNewAuthor] = useState('');
    const [newImageFile, setNewImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState(null);
    const [createSuccess, setCreateSuccess] = useState(null);
    const [isAdmin, setIsAdmin] = useState(localStorage.getItem('hart_admin') === 'true');

    useEffect(() => {
        fetchBlogs();
    }, []);



    const getBlogsEndpoint = () => {
        const apiBase = getApiBase();
        if (apiBase) {
            return `${apiBase}/get_blogs.php`;
        }

        // On localhost without an API URL, fetch the static JSON file directly to avoid console warnings
        if (typeof window !== 'undefined' && /^localhost$|^127\.0\.0\.1$/.test(window.location.hostname)) {
            return '/blogs.json';
        }

        return '/get_blogs.php';
    };

    const logoutAdmin = () => {
        localStorage.removeItem('hart_admin');
        localStorage.removeItem('hart_admin_code');
        setIsAdmin(false);
    };

    const parseJsonResponse = async (response) => {
        const text = await response.text();
        try {
            return JSON.parse(text);
        } catch (error) {
            // returning empty array when HTML/plain text is returned preserves usability
            console.warn('Non-JSON response from blogs endpoint, returning empty array');
            return [];
        }
    };

    const fetchBlogs = async () => {
        try {
            setError(null);
            setLoading(true);
            console.log('Fetching blogs from API...');

            const endpoint = getBlogsEndpoint();
            console.log('Blog API endpoint:', endpoint);
            
            let data = null;
            let phpFetchFailed = false;

            try {
                const response = await fetch(endpoint, { cache: 'no-store' });
                if (!response.ok) {
                    throw new Error(`HTTP error ${response.status}`);
                }
                const text = await response.text();
                data = JSON.parse(text);
                if (!Array.isArray(data)) {
                    throw new Error('Response is not a JSON array');
                }
            } catch (err) {
                console.warn('Failed to fetch/parse blogs from PHP endpoint:', err);
                phpFetchFailed = true;
            }

            if (phpFetchFailed) {
                console.log('Attempting static fallback to /blogs.json...');
                try {
                    const fallbackResponse = await fetch('/blogs.json', { cache: 'no-store' });
                    if (fallbackResponse.ok) {
                        const fallbackText = await fallbackResponse.text();
                        const fallbackData = JSON.parse(fallbackText);
                        if (Array.isArray(fallbackData)) {
                            data = fallbackData;
                            console.log('Loaded blogs from static /blogs.json successfully');
                        } else {
                            console.warn('Fallback blogs.json is not an array');
                        }
                    } else {
                        console.error('Static fallback response not OK:', fallbackResponse.status);
                    }
                } catch (fallbackErr) {
                    console.error('Error fetching static /blogs.json fallback:', fallbackErr);
                }
            }

            // Merge with local storage state (for offline/local-only mode functionality)
            let finalBlogs = Array.isArray(data) ? [...data] : [];
            
            try {
                const deletedIds = JSON.parse(localStorage.getItem('hart_deleted_blogs') || '[]');
                const localBlogs = JSON.parse(localStorage.getItem('hart_local_blogs') || '[]');
                
                // Filter out local deleted ones
                finalBlogs = finalBlogs.filter(b => !deletedIds.includes(b.id));
                
                // Add local created ones
                finalBlogs = [...finalBlogs, ...localBlogs];
            } catch (storageErr) {
                console.error('Error merging local storage blogs:', storageErr);
            }

            // Sort blogs by created_at DESC
            const sortedData = finalBlogs.sort((a, b) => {
                const dateA = a.created_at || '';
                const dateB = b.created_at || '';
                return dateB.localeCompare(dateA);
            });
            setBlogs(sortedData);
        } catch (err) {
            console.log('Error fetching blogs:', err);
            setError(err.message || 'Unexpected error while loading blogs.');
            setBlogs([]);
        } finally {
            setLoading(false);
        }
    };


    if (loading) return <div className="loading">Chargement des blogs...</div>;
    if (error) return <div className="error">Erreur : {error}</div>;

    const createBlogEndpoint = () => {
        const apiBase = getApiBase();
        if (apiBase) return `${apiBase}/create_blog.php`;
        return '/create_blog.php';
    };

    const deleteBlogEndpoint = () => {
        const apiBase = getApiBase();
        if (apiBase) return `${apiBase}/delete_blog.php`;
        return '/delete_blog.php';
    };

    const isLocalAdminMode = () => {
        return localStorage.getItem('hart_admin_mode') === 'local';
    };

    const deleteBlog = async (id) => {
        if (!window.confirm('Supprimer ce blog ?')) return;
        setCreating(true);
        try {
            const isLocal = isLocalAdminMode() || !getApiBase();
            
            if (isLocal) {
                // Save delete action to local storage
                const deletedIds = JSON.parse(localStorage.getItem('hart_deleted_blogs') || '[]');
                if (!deletedIds.includes(id)) {
                    deletedIds.push(id);
                    localStorage.setItem('hart_deleted_blogs', JSON.stringify(deletedIds));
                }
                
                // Also remove from local created blogs if it was there
                const localBlogs = JSON.parse(localStorage.getItem('hart_local_blogs') || '[]');
                const filteredLocal = localBlogs.filter(b => b.id !== id);
                localStorage.setItem('hart_local_blogs', JSON.stringify(filteredLocal));
                
                setCreateSuccess('Blog supprimé localement.');
                await fetchBlogs();
            } else {
                const response = await fetch(deleteBlogEndpoint(), {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ 
                        id, 
                        admin_code: localStorage.getItem('hart_admin_code') || '' 
                    }),
                });
                if (!response.ok) {
                    const text = await response.text().catch(() => 'Unable to get backend error');
                    throw new Error(`Delete failed ${response.status} - ${text}`);
                }
                const result = await parseJsonResponse(response);
                if (result.error) throw new Error(result.error);
                setCreateSuccess('Blog supprimé avec succès.');
                await fetchBlogs();
            }
        } catch (err) {
            setCreateError(err.message || 'Échec de la suppression du blog');
        } finally {
            setCreating(false);
        }
    };

    const submitNewBlog = async (event) => {
        event.preventDefault();
        setCreateError(null);
        setCreateSuccess(null);

        if (!newTitle.trim() || !newContent.trim()) {
            setCreateError('Le titre et le contenu sont obligatoires.');
            return;
        }

        setCreating(true);
        try {
            const isLocal = isLocalAdminMode() || !getApiBase();
            
            if (isLocal) {
                // Create blog object
                const newBlog = {
                    id: 'local_' + Date.now(),
                    title: newTitle.trim(),
                    content: newContent.trim(),
                    author: newAuthor.trim() || 'Admin',
                    image_url: imagePreview || '/images/about1.jpg',
                    created_at: new Date().toISOString().slice(0, 19).replace('T', ' ')
                };
                
                const localBlogs = JSON.parse(localStorage.getItem('hart_local_blogs') || '[]');
                localBlogs.push(newBlog);
                localStorage.setItem('hart_local_blogs', JSON.stringify(localBlogs));
                
                setCreateSuccess('Blog créé localement (enregistré dans le navigateur).');
                setNewTitle('');
                setNewContent('');
                setNewAuthor('');
                setNewImageFile(null);
                setImagePreview(null);
                await fetchBlogs();
            } else {
                const formData = new FormData();
                formData.append('title', newTitle.trim());
                formData.append('content', newContent.trim());
                formData.append('author', newAuthor.trim() || 'Admin');
                formData.append('admin_code', localStorage.getItem('hart_admin_code') || '');
                if (newImageFile) {
                    formData.append('image_file', newImageFile);
                }

                const response = await fetch(createBlogEndpoint(), {
                    method: 'POST',
                    body: formData,
                });

                if (!response.ok) {
                    const text = await response.text().catch(() => 'Unable to get backend error');
                    throw new Error(`Create failed ${response.status} - ${text}`);
                }

                const result = await parseJsonResponse(response);
                if (result.error) {
                    throw new Error(result.error);
                }

                setCreateSuccess('Blog créé avec succès.');
                setNewTitle('');
                setNewContent('');
                setNewAuthor('');
                setNewImageFile(null);
                setImagePreview(null);
                await fetchBlogs();
            }
        } catch (err) {
            setCreateError(err.message || 'Échec de la création du blog');
        } finally {
            setCreating(false);
        }
    };

    const handleImageFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setNewImageFile(file);
            // Create preview
            const reader = new FileReader();
            reader.onload = (event) => {
                setImagePreview(event.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    return (
        <div className="home-container">
            {isAdmin ? (
                <div className="blog-create-zone">
                    <h2>Créer un nouveau blog</h2>
                    <form onSubmit={submitNewBlog} className="blog-create-form">
                    <input type="text" value={newTitle} placeholder="Titre" onChange={(e) => setNewTitle(e.target.value)} required />
                    <input type="text" value={newAuthor} placeholder="Auteur (par défaut: Admin)" onChange={(e) => setNewAuthor(e.target.value)} />
                    <div className="file-input-wrapper">
                        <label htmlFor="image-upload">Télécharger une image (optionnel)</label>
                        <input 
                            id="image-upload"
                            type="file" 
                            accept="image/*" 
                            onChange={handleImageFileChange}
                        />
                        {imagePreview && (
                            <div className="image-preview">
                                <img src={imagePreview} alt="Preview" />
                                <button type="button" onClick={() => { setNewImageFile(null); setImagePreview(null); }}>Supprimer</button>
                            </div>
                        )}
                    </div>
                    <textarea value={newContent} placeholder="Contenu" onChange={(e) => setNewContent(e.target.value)} required rows={5} />
                    <button type="submit" disabled={creating}>{creating ? 'Création...' : 'Créer le blog'}</button>
                    </form>
                    {createError && <div className="error">{createError}</div>}
                    {createSuccess && <div className="success">{createSuccess}</div>}
                    <button type="button" className="blog-delete-button" onClick={logoutAdmin}>Déconnexion</button>
                </div>
            ) : null}

            {/* Blog Header */}
            <div className="blogs-header">
                <h1 className="blogs-title">Nos Histoires</h1>
                <p className="blogs-subtitle">Découvrez les insights et l'expertise de l'équipe Hart Élégance</p>
                <div className="header-divider"></div>
            </div>

            {/* Blogs List */}
            <div className="blogs-masonry">
                {blogs.length === 0 ? (
                    <div className="no-blogs">Aucun blog disponible.</div>
                ) : (
                    blogs.map((blog, index) => (
                        <article 
                            key={blog.id} 
                            className={`masonry-card ${index === 0 ? 'masonry-featured' : ''}`} 
                            onClick={() => setSelectedBlog(blog)}
                        >
                            <div className="masonry-image">
                                <img src={resolveImage(blog.image_url || '/images/about1.jpg')} alt={blog.title} />
                                <div className="masonry-overlay"></div>
                            </div>
                            <div className="masonry-content">
                                {index === 0 && <span className="masonry-badge">À la une</span>}
                                <h3>{blog.title}</h3>
                                {index === 0 && <p className="masonry-preview">{blog.content.substring(0, 120)}...</p>}
                                <div className="masonry-meta">
                                    <span className="masonry-author">{blog.author}</span>
                                    <span className="masonry-date">{new Date(blog.created_at).toLocaleDateString('fr-FR', { month: 'short', day: 'numeric' })}</span>
                                </div>
                                {isAdmin && (
                                    <button
                                        type="button"
                                        className="blog-delete-button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            deleteBlog(blog.id);
                                        }}
                                    >
                                        Delete
                                    </button>
                                )}
                            </div>
                        </article>
                    ))
                )}
            </div>

            {selectedBlog && (
                <PopupCard
                    open={true}
                    title={selectedBlog.title}
                    image={resolveImage(selectedBlog.image_url || '/images/about1.jpg')}
                    text={`By ${selectedBlog.author} - ${new Date(selectedBlog.created_at).toLocaleDateString()}\n\n${selectedBlog.content}`}
                    onClose={() => setSelectedBlog(null)}
                />
            )}
        </div>
    );
}

export default Blogs;
